import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  isSameDay,
  addMonths,
  startOfDay,
  isBefore,
} from "date-fns";
import { ru } from "date-fns/locale";
import clsx from "clsx";
import styles from "../styles/DateTimeDropdown.module.css";

/**
 * mode:
 *  - "date" -> returns "YYYY-MM-DD"
 *  - "time" -> returns "HH:mm"
 */
export default function DateTimeDropdown({
                                           value = null,
                                           onChange,
                                           timeStep = 30,
                                           placeholder = "Дата",
                                           mode = "date",
                                         }) {
  const [open, setOpen] = useState(false);

  // date mode states
  const [pickedDate, setPickedDate] = useState(() => {
    if (mode !== "date") return null;
    if (!value) return null;
    return new Date(value + "T00:00:00");
  });

  const [monthOffset, setMonthOffset] = useState(0);

  // time mode state
  const [selectedTime, setSelectedTime] = useState(() => {
    if (mode !== "time") return "";
    return value || "";
  });

  const wrapperRef = useRef(null);
  const timeListRef = useRef(null);
  const selectedTimeItemRef = useRef(null);

  const today = useMemo(() => startOfDay(new Date()), []);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // sync external value
  useEffect(() => {
    if (mode === "date") {
      if (!value) setPickedDate(null);
      else setPickedDate(new Date(value + "T00:00:00"));
    }
    if (mode === "time") {
      setSelectedTime(value || "");
    }
  }, [value, mode]);

  const daysInMonth = useMemo(() => {
    if (mode !== "date") return [];
    const firstDay = startOfMonth(addMonths(new Date(), monthOffset));
    const lastDay = endOfMonth(addMonths(new Date(), monthOffset));
    return eachDayOfInterval({ start: firstDay, end: lastDay });
  }, [monthOffset, mode]);

  const times = useMemo(() => {
    if (mode !== "time") return [];
    const arr = [];
    for (let m = 0; m < 24 * 60; m += timeStep) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      arr.push(`${h.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`);
    }
    return arr;
  }, [timeStep, mode]);

  // автоскролл к выбранному времени при открытии time-dropdown
  useEffect(() => {
    if (mode !== "time" || !open) return;
    selectedTimeItemRef.current?.scrollIntoView({
      block: "nearest",
    });
  }, [open, mode, selectedTime]);

  const firstDayOfWeek = () => {
    if (!daysInMonth?.length) return 0;
    const first = daysInMonth[0].getDay();
    return first === 0 ? 6 : first - 1;
  };

  const weekHeader = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

  const now = new Date();
  const monthNames = [
    format(addMonths(now, 0), "LLLL", { locale: ru }),
    format(addMonths(now, 1), "LLLL", { locale: ru }),
  ];

  const displayValue = (() => {
    if (mode === "date") {
      return pickedDate ? format(pickedDate, "dd.MM.yyyy", { locale: ru }) : "";
    }
    if (mode === "time") {
      return selectedTime || "";
    }
    return "";
  })();

  const handleDateClick = (day) => {
    // запрет выбора прошлых дат
    if (isBefore(startOfDay(day), today)) return;

    setPickedDate(day);
    const isoDate = format(day, "yyyy-MM-dd");
    onChange?.(isoDate);
    setOpen(false);
  };

  const handleTimePick = (t) => {
    setSelectedTime(t);
    if (!t) return;
    onChange?.(t);
    setOpen(false);
  };

  return (
      <div className={styles.wrapper} ref={wrapperRef}>
        <div
            className={clsx(styles.input, { [styles.placeholder]: !displayValue }) + " input"}
            onClick={() => setOpen((prev) => !prev)}
        >
          {displayValue || placeholder}
          <span className={styles.icon} />
        </div>

        {/* TIME MODE */}
        {open && mode === "time" && (
            <div className={`${styles.dropdown} p-0`}>
              <div
                  ref={timeListRef}
                  style={{
                    maxHeight: 220,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
              >
                {times.map((t) => {
                  const active = t === selectedTime;
                  return (
                      <button
                          key={t}
                          type="button"
                          ref={active ? selectedTimeItemRef : null}
                          onClick={() => handleTimePick(t)}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            padding: "8px 12px",
                            border: "0",
                            background: active ? "var(--fill-green)" : "transparent",
                            color: active ? "#16120F" : "var(--font-green)",
                            cursor: "pointer",
                          }}
                      >
                        {t}
                      </button>
                  );
                })}
              </div>
            </div>
        )}

        {/* DATE MODE */}
        {open && mode === "date" && (
            <div className={styles.dropdown}>
              <div className={styles.monthHeader}>
                <div>
              <span
                  className={clsx(styles.monthName, { [styles.monthActive]: monthOffset === 0 })}
                  onClick={() => setMonthOffset(0)}
                  style={{
                    cursor: monthOffset !== 0 ? "pointer" : "default",
                    opacity: monthOffset === 0 ? 1 : 0.5,
                  }}
              >
                {monthNames[0][0].toUpperCase() + monthNames[0].slice(1)}
              </span>

                  <span
                      className={clsx(styles.monthName, { [styles.monthActive]: monthOffset === 1 })}
                      onClick={() => setMonthOffset(1)}
                      style={{
                        cursor: monthOffset !== 1 ? "pointer" : "default",
                        opacity: monthOffset === 1 ? 1 : 0.5,
                        marginLeft: 16,
                      }}
                  >
                {monthNames[1][0].toUpperCase() + monthNames[1].slice(1)}
              </span>
                </div>
              </div>

              <div className={styles.weekHeader}>
                {weekHeader.map((d) => (
                    <span key={d}>{d}</span>
                ))}
              </div>

              <div className={styles.daysGrid}>
                {Array.from({ length: firstDayOfWeek() }).map((_, idx) => (
                    <span key={idx} />
                ))}

                {daysInMonth.map((day) => {
                  const disabled = isBefore(startOfDay(day), today);

                  return (
                      <button
                          key={day.toISOString()}
                          type="button"
                          className={clsx(styles.dayCell, {
                            [styles.selected]: pickedDate && isSameDay(day, pickedDate),
                          })}
                  disabled={disabled}
                  onClick={() => handleDateClick(day)}
                                   style={{
                                        opacity: disabled ? 0.35 : 1,
                                            cursor: disabled ? "not-allowed" : "pointer",
                                      }}
                >
                  {format(day, "d")}
                </button>
                );
                })}
              </div>
            </div>
        )}
      </div>
  );
}